import { DropdownMenu as MenuPrimitive } from 'radix-ui';
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';

import { cn } from '../cn';
import { CheckIcon } from '../icons';

export const DropdownMenu = MenuPrimitive.Root;
export const DropdownMenuTrigger = MenuPrimitive.Trigger;
export const DropdownMenuGroup = MenuPrimitive.Group;
export const DropdownMenuSub = MenuPrimitive.Sub;

export const DropdownMenuContent = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof MenuPrimitive.Content>>(
  function DropdownMenuContent({ className, sideOffset = 4, align = 'end', ...props }, ref) {
    return (
      <MenuPrimitive.Portal>
        <MenuPrimitive.Content
          ref={ref}
          sideOffset={sideOffset}
          align={align}
          collisionPadding={8}
          className={cn(
            'z-(--z-popover) min-w-48 overflow-hidden rounded-md border border-border bg-surface p-1 text-ink shadow-overlay data-[state=open]:animate-fade-in',
            className,
          )}
          {...props}
        />
      </MenuPrimitive.Portal>
    );
  },
);

const itemClass =
  'relative flex h-8 items-center gap-2 rounded-sm px-2 text-body outline-none select-none data-[disabled]:opacity-50 data-[highlighted]:bg-surface-muted';

export const DropdownMenuItem = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof MenuPrimitive.Item> & { tone?: 'default' | 'danger'; shortcut?: string; hint?: ReactNode }
>(function DropdownMenuItem({ className, tone = 'default', shortcut, hint, children, ...props }, ref) {
  return (
    <MenuPrimitive.Item ref={ref} className={cn(itemClass, tone === 'danger' && 'text-negative', className)} {...props}>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate">{children}</span>
      </span>
      {hint ? <span className="text-meta text-ink-muted">{hint}</span> : null}
      {shortcut ? <span className="ms-auto text-meta text-ink-muted">{shortcut}</span> : null}
    </MenuPrimitive.Item>
  );
});

export const DropdownMenuCheckboxItem = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof MenuPrimitive.CheckboxItem>>(
  function DropdownMenuCheckboxItem({ className, children, ...props }, ref) {
    return (
      <MenuPrimitive.CheckboxItem ref={ref} className={cn(itemClass, 'ps-7', className)} {...props}>
        <span className="absolute start-2 flex items-center">
          <MenuPrimitive.ItemIndicator>
            <CheckIcon size={14} className="text-accent" />
          </MenuPrimitive.ItemIndicator>
        </span>
        {children}
      </MenuPrimitive.CheckboxItem>
    );
  },
);

export const DropdownMenuRadioGroup = MenuPrimitive.RadioGroup;
export const DropdownMenuRadioItem = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof MenuPrimitive.RadioItem>>(
  function DropdownMenuRadioItem({ className, children, ...props }, ref) {
    return (
      <MenuPrimitive.RadioItem ref={ref} className={cn(itemClass, 'ps-7', className)} {...props}>
        <span className="absolute start-2 flex items-center">
          <MenuPrimitive.ItemIndicator>
            <CheckIcon size={14} className="text-accent" />
          </MenuPrimitive.ItemIndicator>
        </span>
        {children}
      </MenuPrimitive.RadioItem>
    );
  },
);

export function DropdownMenuLabel({ children, className }: { children: ReactNode; className?: string }) {
  return <MenuPrimitive.Label className={cn('px-2 pt-1.5 pb-1 text-meta font-medium text-ink-muted', className)}>{children}</MenuPrimitive.Label>;
}

export function DropdownMenuSeparator() {
  return <MenuPrimitive.Separator className="-mx-1 my-1 h-px bg-border" />;
}
